/**
 * `BillingHistoryCard` — ops billing lifecycle history for an account
 * (ADR-0155). Renders the account's `admin_audit_log` billing rows (checkout /
 * downgrade / webhook plan change), newest first. Read-only.
 */

import type { AdminBillingEvent } from '@sill/types/api';

import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const ACTION_LABEL: Record<AdminBillingEvent['action'], string> = {
  billing_checkout_started: 'Checkout started',
  billing_downgrade_scheduled: 'Downgrade scheduled',
  billing_plan_changed: 'Plan changed',
};

function fmtUtc(iso: string): string {
  try {
    const d = new Date(iso);
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
  } catch {
    return iso;
  }
}

function summarize(e: AdminBillingEvent): string {
  const c = e.context;
  if (e.action === 'billing_plan_changed') {
    const dir = typeof c.direction === 'string' ? c.direction : '';
    const from = typeof c.from_status === 'string' ? c.from_status : '—';
    const to = typeof c.to_status === 'string' ? c.to_status : '—';
    return `${dir}${dir.length > 0 ? ' · ' : ''}${from} → ${to}`;
  }
  if (e.action === 'billing_checkout_started') {
    const plan = typeof c.plan_id === 'string' ? c.plan_id : '';
    const iv = typeof c.interval === 'string' ? c.interval : '';
    return [plan, iv].filter((s) => s.length > 0).join(' · ');
  }
  return '';
}

export function BillingHistoryCard({ events }: { events: readonly AdminBillingEvent[] }): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Billing history
        </div>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, letterSpacing: 1 }}>
          {events.length === 0 ? 'no events' : `${events.length} recent`}
        </div>
      </div>

      {events.length === 0 ? (
        <div
          className="p-3"
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          No billing activity yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-1">
          {events.map((e, idx) => {
            const detail = summarize(e);
            return (
              <div
                key={e.log_id}
                className="flex items-start justify-between gap-3 py-2 flex-wrap"
                style={{ borderTop: idx > 0 ? `1px solid ${C.borderSoft}` : 'none' }}
              >
                <div className="min-w-0">
                  <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                    {ACTION_LABEL[e.action]}
                    {detail.length > 0 ? (
                      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginLeft: 8 }}>
                        {detail}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontFamily: monoFont, fontSize: 11, color: C.dim, marginTop: 2 }}>
                    {e.actor_user_id === null ? 'system / webhook' : `user ${e.actor_user_id}`}
                  </div>
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>
                  {fmtUtc(e.created_at)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
